import { type FC, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui';
import { Input } from '@/components/ui';
import { Label } from '@/components/ui';
import { Button } from '@/components/ui';
import { useAuth } from '@/hooks';
import { CheckCircle } from 'lucide-react';

/**
 * Forgot password page - allows users to request a password reset email
 */
export const ForgotPasswordPage: FC = () => {
  const { resetPassword, loading, error } = useAuth();

  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setFormError(null);

    // Basic validation
    if (!email) {
      setFormError('Please enter your email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    try {
      await resetPassword(email);
      setSubmitted(true);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to send reset email. Please try again.'
      );
    }
  };

  // Success state after email is sent
  if (submitted) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto">
            <Card className="bg-white border-turquoise-200 shadow-md">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <CheckCircle className="h-16 w-16 text-green-500" />
                </div>
                <CardTitle className="text-3xl font-viking text-turquoise-900">Check Your Email</CardTitle>
                <CardDescription className="text-turquoise-700">
                  We've sent password reset instructions to your email
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-turquoise-700 text-center">
                  If an account exists for <span className="font-medium">{email}</span>, you will receive an email with instructions to reset your password.
                </p>
                <p className="text-sm text-turquoise-600 text-center">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <div className="flex flex-col gap-2 pt-4">
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setEmail('');
                    }}
                    variant="secondary"
                    className="w-full"
                  >
                    Try Different Email
                  </Button>
                  <Link to="/login" className="w-full">
                    <Button variant="ghost" className="w-full">
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <Card className="bg-white border-turquoise-200 shadow-md">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-viking text-turquoise-900">Forgot Password</CardTitle>
              <CardDescription className="text-turquoise-700">
                Enter your email and we'll send you reset instructions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Error message */}
                {(formError || error) && (
                  <div className="bg-red-900/40 text-red-200 text-sm p-3 rounded-md border border-red-500/40">
                    {formError || error?.message}
                  </div>
                )}

                {/* Email field */}
                <div className="space-y-2">
                  <Label htmlFor="email" required className="text-turquoise-900">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                    required
                  />
                </div>

                {/* Submit button */}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                {/* Back to login link */}
                <div className="text-center text-sm text-turquoise-700">
                  Remember your password?{' '}
                  <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">
                    Sign in
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
